import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Push, OTP, and passwordless in one app',
    description:
      'A single branded mobile authenticator covers every flow your users will see: approve a push, type an OTP, or sign in passwordlessly.',
    bullets: [
      'One-tap push approval',
      'Time-based one-time passwords (TOTP)',
      'Passwordless sign-in with device-bound keys',
      'QR-based desktop login',
    ],
  },
  {
    title: 'Device-bound, hardware-anchored',
    description:
      'Cryptographic keys live in the device secure enclave. Even if the OS is compromised, the keys can not be exfiltrated or replayed.',
    bullets: [
      'Secure Enclave / StrongBox key storage',
      'Biometric unlock (Face ID / Touch ID / Android biometrics)',
      'Tamper-detection on rooted / jailbroken devices',
      'Device attestation as a policy signal',
    ],
  },
  {
    title: 'Your brand, your support burden: zero',
    description:
      'White-label the app to match your company. Users do not have to learn another vendor name; your help desk does not field "what app do I install?" tickets.',
    bullets: [
      'Custom logo, colors, and app name',
      'In-app help and support links to your portal',
      'Multilingual UI (EN, TR, AR, more)',
      'Hosted on App Store and Google Play under your brand',
    ],
  },
  {
    title: 'Offline OTP, online intelligence',
    description:
      'OTPs work without connectivity for travelers and on-prem ops. Push flows add device context (IP, geo, app), so users approve with eyes open.',
    bullets: [
      'Offline TOTP generation',
      'Rich push: which app, from where, what device',
      'Geo-anomaly warnings before approval',
      'Tap-to-deny + automatic incident escalation',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Brand it',
    description:
      'Upload your logo and colors. Pick the bundle ID. Monofor publishes the app to App Store and Google Play.',
  },
  {
    title: 'Enroll users',
    description:
      'Users scan a QR from their Monosign portal. Enrollment binds the device to the user in under a minute.',
  },
  {
    title: 'Authenticate from anywhere',
    description:
      'Push, OTP, biometric, or QR: pick the flow that fits the moment. Every challenge is logged on the Monosign side.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Multi-Factor Authentication',
    description:
      'FIDO2, push, OTP, and hardware-key flows with adaptive policy.',
    href: '/sign/mfa',
  },
  {
    label: 'Single Sign-On',
    description:
      'One identity across every app: SAML 2, OIDC, OAuth 2.',
    href: '/sign/sso',
  },
  {
    label: 'Passwordless MFA',
    description:
      'Use case: getting passwords out of the day-to-day workflow.',
    href: '/solutions/passwordless-mfa',
  },
]

export default function AuthenticatorPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Monofor Identity: branded authenticator app"
        description="Push, OTP, biometric, and passwordless authentication in one white-labeled mobile app. Device-bound keys, offline OTP, your brand."
        path="/sign/authenticator"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Monofor Identity"
          title={
            <>
              Your authenticator,
              <br />
              your brand.
            </>
          }
          body="Push, OTP, biometric, and passwordless flows in a single white-label mobile app. Hardware-backed keys, offline support, and rich device context, published under your name."
          badges={[
            'Push, OTP, passwordless',
            'Hardware-backed keys',
            'Your logo, your bundle ID',
          ]}
        />
        <SolutionPillars
          title={<>The authenticator your users will not uninstall.</>}
          body="Most MFA pain comes from users juggling unbranded vendor apps. Monofor Identity lives under your brand, on the user's home screen, with the flows your security team actually needs."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Brand it. Ship it. Trust it.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
